import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { formatCurrency, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "@/components/ui/select";
import TransactionForm from "@/components/TransactionForm";
import MultiTransactionForm from "@/components/transactions/MultiTransactionForm";
import TransferDialog from "@/components/TransferDialog";
import SplitTransactionDialog from "@/components/SplitTransactionDialog";
import { Pencil, Trash2, Plus, ArrowDownRight, ArrowUpRight, PiggyBank, Download, Upload, CheckSquare, Square, X, ChevronDown, Building2, Tag, FileDown, Sparkles, ArrowLeftRight, Split } from "lucide-react";
import { toast } from "sonner";
import { useEntity } from "@/context/EntityContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";

export default function Transactions() {
  const { user } = useAuth();
  const currency = user?.currency || "USD";
  const { displayAmount } = useCurrency() || {};
  const [txs, setTxs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [accountFilter, setAccountFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [splitTarget, setSplitTarget] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTargetId, setMoveTargetId] = useState("");
  const [recatOpen, setRecatOpen] = useState(false);
  const [recatCategory, setRecatCategory] = useState("");
  const [bulkCategories, setBulkCategories] = useState({ expense: [], income: [], saving: [] });
  const { entities, currentEntityId, currentEntity } = useEntity();
  const isBusiness = currentEntity?.workspace_type === "business";
  const catLabel = isBusiness ? "department" : "category";
  const catLabelTitle = isBusiness ? "Department" : "Category";
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    const params = {};
    if (typeFilter !== "all") params.type = typeFilter;
    if (accountFilter !== "all") params.account_id = accountFilter;
    try {
      const [{ data }, { data: accs }] = await Promise.all([
        api.get("/transactions", { params }),
        api.get("/accounts"),
      ]);
      setTxs(data);
      setAccounts(accs);
    } catch (err) {
      console.warn("[Transactions] load failed:", err?.message);
      toast.error("Couldn't load transactions");
    }
  }, [typeFilter, accountFilter]);

  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (!deleteId) return;
    await api.delete(`/transactions/${deleteId}`);
    toast.success("Transaction deleted");
    setDeleteId(null);
    load();
  };

  const accountName = (id) => accounts.find((a) => a.id === id)?.name || "";

  const filtered = txs.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return t.category.toLowerCase().includes(q) || (t.note || "").toLowerCase().includes(q);
  });

  // ---------- Selection helpers ----------
  const enterSelectMode = () => setSelectMode(true);
  const cancelSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
  };
  const toggleOne = (id, index, shift) => {
    const next = new Set(selectedIds);
    if (shift && lastSelectedIndex != null && lastSelectedIndex !== index) {
      // Range select from lastSelectedIndex..index across the currently filtered list
      const [lo, hi] = index < lastSelectedIndex
        ? [index, lastSelectedIndex]
        : [lastSelectedIndex, index];
      const wantSelected = !selectedIds.has(id) || !next.has(filtered[lo].id);
      for (let i = lo; i <= hi; i += 1) {
        if (wantSelected) next.add(filtered[i].id);
        else next.delete(filtered[i].id);
      }
    } else if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
    setLastSelectedIndex(index);
  };
  const allFilteredSelected = filtered.length > 0 && filtered.every((t) => selectedIds.has(t.id));
  const toggleAllFiltered = () => {
    if (allFilteredSelected) {
      const next = new Set(selectedIds);
      filtered.forEach((t) => next.delete(t.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      filtered.forEach((t) => next.add(t.id));
      setSelectedIds(next);
    }
  };
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const { data } = await api.post("/transactions/bulk-delete", { ids });
      showUndoToast({
        message: `Deleted ${data.deleted} transaction${data.deleted === 1 ? "" : "s"}`,
        opId: data.op_id,
      });
      cancelSelectMode();
      setBulkDeleteOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    }
  };

  const openMove = async () => {
    setMoveTargetId("");
    setMoveOpen(true);
  };

  const bulkMove = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !moveTargetId) return;
    try {
      const { data } = await api.post("/transactions/bulk-move", { ids, target_entity_id: moveTargetId });
      showUndoToast({
        message: `Moved ${data.moved} to ${data.target_entity_name}`,
        opId: data.op_id,
      });
      cancelSelectMode();
      setMoveOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Move failed");
    }
  };

  const openRecategorize = async () => {
    setRecatCategory("");
    setRecatOpen(true);
    try {
      const { data } = await api.get("/categories");
      setBulkCategories(data);
    } catch (err) {
      console.warn("[Transactions] load categories failed:", err?.message);
      toast.error("Couldn't load categories");
    }
  };

  const bulkRecategorize = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0 || !recatCategory) return;
    try {
      const { data } = await api.post("/transactions/bulk-recategorize", { ids, category: recatCategory });
      showUndoToast({
        message: `Recategorized ${data.updated} to ${data.category}`,
        opId: data.op_id,
      });
      cancelSelectMode();
      setRecatOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Recategorize failed");
    }
  };

  const showUndoToast = ({ message, opId }) => {
    if (!opId) {
      toast.success(message);
      return;
    }
    toast.success(message, {
      duration: 5000,
      action: {
        label: "Undo",
        onClick: async () => {
          try {
            const { data } = await api.post(`/transactions/bulk-undo/${opId}`);
            toast.success(`Undone · ${data.restored} restored`);
            load();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Undo failed");
          }
        },
      },
    });
  };

  const bulkExport = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const res = await api.post("/transactions/bulk-export.csv", { ids }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `transactions_selected_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${ids.length} transaction${ids.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    }
  };

  const bulkAiCategorize = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    toast.info(`AI is analyzing ${ids.length} transaction${ids.length === 1 ? "" : "s"}…`);
    try {
      const { data } = await api.post("/ai/bulk-suggest-category", { tx_ids: ids });
      let applied = 0;
      for (const r of data.results || []) {
        if (r.category && r.confidence >= 0.5) {
          await api.patch(`/transactions/${r.id}`, { category: r.category });
          applied += 1;
        }
      }
      toast.success(`AI recategorized ${applied} of ${data.results.length} transactions`);
      cancelSelectMode();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI categorize failed");
    }
  };

  const typeChip = (type) => (
    type === "income" ? { c: "bg-[#EAF4EE] text-[#4C956C]", I: ArrowUpRight } :
    type === "expense" ? { c: "bg-[#FCE7E9] text-[#E63946]", I: ArrowDownRight } :
    { c: "bg-[#F5EAE3] text-[#E07A5F]", I: PiggyBank }
  );

  const exportCsv = () => {
    // Anchor download; cookie auth carries over with same-origin/CORS credentials
    const url = `${API}/transactions/export.csv`;
    const a = document.createElement("a");
    a.href = url;
    a.download = "transactions.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/transactions/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.inserted} · Skipped ${data.skipped}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Records</div>
          <h1 className="font-display text-4xl mt-2">Transactions</h1>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileInputRef}
            data-testid="csv-import-input"
            type="file"
            accept=".csv"
            onChange={importCsv}
            className="hidden"
          />
          <Button
            data-testid="csv-import-btn"
            variant="outline"
            className="rounded-full h-11 px-4"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button
            data-testid="csv-export-btn"
            variant="outline"
            className="rounded-full h-11 px-4"
            onClick={exportCsv}
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          {!selectMode ? (
            <Button
              data-testid="select-mode-btn"
              variant="outline"
              className="rounded-full h-11 px-4"
              onClick={enterSelectMode}
            >
              <CheckSquare className="w-4 h-4 mr-2" /> Select
            </Button>
          ) : (
            <Button
              data-testid="select-cancel-btn"
              variant="outline"
              className="rounded-full h-11 px-4"
              onClick={cancelSelectMode}
            >
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-transaction-btn-page" className="rounded-md btn-brand h-11 px-5">
                <Plus className="w-4 h-4 mr-2" /> Add transaction
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-transaction-dialog-page" className="w-[96vw] max-w-[1240px] sm:max-w-[1240px]">
              <DialogHeader><DialogTitle>New transactions</DialogTitle>
<DialogDescription className="text-xs">Enter one or more lines, then save them together.</DialogDescription></DialogHeader>
              <MultiTransactionForm
                currency={currency}
                onDone={(n) => { setAddOpen(false); toast.success(`${n} transaction${n === 1 ? "" : "s"} added`); load(); }}
                onCancel={() => setAddOpen(false)}
              />
            </DialogContent>
          </Dialog>
          <Button
            data-testid="new-transfer-btn"
            variant="outline"
            className="rounded-full h-11 px-4"
            onClick={() => setTransferOpen(true)}
          >
            <ArrowLeftRight className="w-4 h-4 mr-2" /> Transfer
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6 flex-wrap">
        <Input
          data-testid="tx-search"
          placeholder="Search by category or note..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="tx-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="filter-all">All types</SelectItem>
            <SelectItem value="expense" data-testid="filter-expense">Expenses</SelectItem>
            <SelectItem value="income" data-testid="filter-income">Income</SelectItem>
            <SelectItem value="saving" data-testid="filter-saving">Savings</SelectItem>
          </SelectContent>
        </Select>
        <Select value={accountFilter} onValueChange={setAccountFilter}>
          <SelectTrigger data-testid="tx-account-filter" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All accounts</SelectItem>
            {accounts.map((a) => (
              <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectMode && (
        <div
          data-testid="bulk-action-bar"
          className="card-flat p-3 mb-4 border-l-4 border-l-[#D4A017] flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="bulk-select-all"
              onClick={toggleAllFiltered}
              className="inline-flex items-center gap-2 text-sm"
            >
              {allFilteredSelected ? <CheckSquare className="w-4 h-4 text-[#2F4F4F]" /> : <Square className="w-4 h-4 text-[var(--muted-text)]" />}
              {allFilteredSelected ? "Deselect all visible" : "Select all visible"}
            </button>
            <span data-testid="bulk-selected-count" className="text-sm text-[var(--muted-text)]">
              {selectedIds.size} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[var(--muted-text)] hidden md:inline">
              Tip: shift-click a checkbox to select a range.
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="bulk-actions-btn"
                  disabled={selectedIds.size === 0}
                  className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8] h-10 px-4 gap-2"
                >
                  Bulk actions{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem data-testid="bulk-action-move" onClick={openMove}>
                  <Building2 className="w-4 h-4 mr-2" /> Move to entity…
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="bulk-action-recategorize" onClick={openRecategorize}>
                  <Tag className="w-4 h-4 mr-2" /> Change {catLabel}…
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="bulk-action-ai-categorize" onClick={bulkAiCategorize}>
                  <Sparkles className="w-4 h-4 mr-2" /> AI smart categorize
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="bulk-action-export" onClick={bulkExport}>
                  <FileDown className="w-4 h-4 mr-2" /> Export as CSV
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="bulk-action-delete"
                  onClick={() => setBulkDeleteOpen(true)}
                  className="text-[#E63946] focus:text-[#E63946] focus:bg-[#FCE7E9]"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      <div className="card-flat overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 border-b border-[var(--border)] text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)]">
          {selectMode ? (
            <div className="col-span-1">
              <button
                type="button"
                data-testid="header-select-toggle"
                onClick={toggleAllFiltered}
                className="inline-flex"
                aria-label="Toggle all"
              >
                {allFilteredSelected ? <CheckSquare className="w-4 h-4 text-[#2F4F4F]" /> : <Square className="w-4 h-4 text-[var(--muted-text)]" />}
              </button>
            </div>
          ) : null}
          <div className={selectMode ? "col-span-2" : "col-span-3"}>Category</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Account</div>
          <div className="col-span-3">Note</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-1" />
        </div>
        <div data-testid="tx-list" className="divide-y divide-[var(--divider)]">
          {filtered.length === 0 && (
            <div className="p-10 text-center text-sm text-[var(--muted-text)]">No transactions match.</div>
          )}
          {filtered.map((tx, idx) => {
            const chip = typeChip(tx.type);
            const Icon = chip.I;
            const isSelected = selectedIds.has(tx.id);
            return (
              <div
                key={tx.id}
                data-testid={`tx-row-${tx.id}`}
                className={`grid grid-cols-12 px-6 py-3 items-center transition-colors ${
                  isSelected ? "bg-[#F5EAE3]" : "hover:bg-[var(--row-hover)]"
                }`}
              >
                {selectMode && (
                  <div className="col-span-1">
                    <button
                      type="button"
                      data-testid={`tx-select-${tx.id}`}
                      onClick={(e) => toggleOne(tx.id, idx, e.shiftKey)}
                      className="inline-flex"
                      aria-label={isSelected ? "Deselect" : "Select"}
                    >
                      {isSelected ? <CheckSquare className="w-4 h-4 text-[#2F4F4F]" /> : <Square className="w-4 h-4 text-[var(--muted-text)]" />}
                    </button>
                  </div>
                )}
                <div className={`${selectMode ? "col-span-2" : "col-span-3"} flex items-center gap-3`}>
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${chip.c}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {tx.category}
                      {tx.has_splits && (
                        <span
                          data-testid={`tx-split-badge-${tx.id}`}
                          className="inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded bg-[var(--brand-light)] text-[var(--brand-text)] uppercase tracking-wider"
                          title={`Split into ${(tx.split_child_ids || []).length} lines`}
                        >
                          <Split className="w-2.5 h-2.5" />
                          Split
                        </span>
                      )}
                      {tx.is_split_child && (
                        <span
                          className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-[var(--hover)] text-[var(--muted-text)] uppercase tracking-wider"
                          title="Part of a split transaction"
                        >
                          Part
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--muted-text)] capitalize">{tx.type}</div>
                  </div>
                </div>
                <div className="col-span-2 text-sm">{formatDate(tx.date)}</div>
                <div className="col-span-2 text-sm text-[var(--muted-text)]">{accountName(tx.account_id) || "—"}</div>
                <div className="col-span-3 text-sm text-[var(--muted-text)] truncate">{tx.note}</div>
                <div className={`col-span-1 text-right font-mono-data text-sm ${
                  tx.type === "income" ? "text-[#4C956C]" : ""
                } ${tx.has_splits ? "line-through opacity-60" : ""}`}>
                  {tx.type === "income" ? "+" : "−"}{displayAmount ? displayAmount(tx.amount, tx.currency) : formatCurrency(tx.amount, tx.currency)}
                </div>
                <div className="col-span-1 flex justify-end gap-1">
                  {!selectMode && (
                    <>
                      {!tx.is_transfer && !tx.is_split_child && (
                        <Button
                          data-testid={`tx-split-${tx.id}`}
                          size="icon"
                          variant="ghost"
                          onClick={() => setSplitTarget(tx)}
                          title={tx.has_splits ? "Manage split" : "Split this transaction"}
                        >
                          <Split className={`w-4 h-4 ${tx.has_splits ? "text-[var(--brand-text)]" : ""}`} />
                        </Button>
                      )}
                      <Button data-testid={`tx-edit-${tx.id}`} size="icon" variant="ghost" onClick={() => setEditing(tx)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button data-testid={`tx-delete-${tx.id}`} size="icon" variant="ghost" onClick={() => setDeleteId(tx.id)}>
                        <Trash2 className="w-4 h-4 text-[#E63946]" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit transaction</DialogTitle></DialogHeader>
          {editing && (
            <TransactionForm
              initial={editing}
              currency={currency}
              onDone={() => { setEditing(null); toast.success("Transaction updated"); load(); }}
              onCancel={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete transaction?</AlertDialogTitle>
            <AlertDialogDescription>
              This can&apos;t be undone. The transaction will be removed from your records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="tx-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="tx-delete-confirm"
              onClick={remove}
              className="bg-[#E63946] hover:bg-[#c92e3b] text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} transaction{selectedIds.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This can&apos;t be undone. All selected transactions will be removed from your records and reflected across every report (P&amp;L, Balance Sheet, Cash Flow, and General Ledger).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="bulk-delete-confirm"
              onClick={bulkDelete}
              className="bg-[#E63946] hover:bg-[#c92e3b] text-white"
            >
              Delete {selectedIds.size}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Move {selectedIds.size} to another entity</DialogTitle>
            <DialogDescription>
              These transactions will disappear from the current view and re-appear under the chosen entity&apos;s books.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">Target entity</Label>
            <Select value={moveTargetId} onValueChange={setMoveTargetId}>
              <SelectTrigger data-testid="bulk-move-target"><SelectValue placeholder="Pick an entity" /></SelectTrigger>
              <SelectContent>
                {entities.filter((e) => e.id !== currentEntityId).map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`bulk-move-opt-${e.name}`}>
                    <span className="inline-flex items-center gap-2">
                      <span style={{ color: e.color }}>{e.icon}</span>{e.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {entities.filter((e) => e.id !== currentEntityId).length === 0 && (
              <p className="text-xs text-[var(--muted-text)]">
                No other entities exist yet. Create one on <b>Entities</b> first.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setMoveOpen(false)}>Cancel</Button>
            <Button
              data-testid="bulk-move-confirm"
              disabled={!moveTargetId}
              onClick={bulkMove}
              className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
            >
              Move {selectedIds.size}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={recatOpen} onOpenChange={setRecatOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change {catLabel} for {selectedIds.size}</DialogTitle>
            <DialogDescription>
              Set a new {catLabel}. This is a hard replace — every selected transaction adopts the chosen {catLabel} regardless of its previous one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-[10px] uppercase tracking-widest text-[var(--muted-text)]">New {catLabel}</Label>
            <Select value={recatCategory} onValueChange={setRecatCategory}>
              <SelectTrigger data-testid="bulk-recat-target"><SelectValue placeholder={`Pick a ${catLabel}`} /></SelectTrigger>
              <SelectContent>
                {["expense", "income", "saving"].map((t) => (
                  (bulkCategories[t] || []).length > 0 && (
                    <SelectGroup key={t}>
                      <SelectLabel className="capitalize">{t}</SelectLabel>
                      {(bulkCategories[t] || []).map((c) => (
                        <SelectItem key={`${t}-${c.id}`} value={c.name} data-testid={`bulk-recat-opt-${c.name}`}>
                          <span className="inline-flex items-center gap-2">
                            <span style={{ color: c.color }}>{c.icon}</span>{c.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setRecatOpen(false)}>Cancel</Button>
            <Button
              data-testid="bulk-recat-confirm"
              disabled={!recatCategory}
              onClick={bulkRecategorize}
              className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
            >
              Recategorize {selectedIds.size} to {catLabelTitle.toLowerCase()}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <TransferDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        onDone={() => { setTransferOpen(false); load(); }}
      />

      <SplitTransactionDialog
        open={!!splitTarget}
        parent={splitTarget}
        onClose={() => setSplitTarget(null)}
        onDone={() => { setSplitTarget(null); load(); }}
      />
    </AppShell>
  );
}
